import { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useApp } from '@contexts/AppContext';
import { useAuth } from '@contexts/AuthContext';
import useMediaQuery from '@hooks/useMediaQuery';
import AccountSwitcher from './AccountSwitcher';

/**
 * Navigation — Left drawer (A5c)
 *
 * Slide-out drawer w-320px with backdrop.
 * Three user states:
 *   Guest:    Sign-in CTA, subset nav, UOM toggle, language selector
 *   Customer: Read-only account display, full nav, active border-right electric-blue
 *   Employee: 3 cascading dropdowns, expanded nav including Administration
 */

const LANGUAGES = [
  { code: 'en', label: 'English', flag: '\u{1F1EC}\u{1F1E7}' },
  { code: 'sv', label: 'Svenska', flag: '\u{1F1F8}\u{1F1EA}' },
  { code: 'de', label: 'Deutsch', flag: '\u{1F1E9}\u{1F1EA}' },
  { code: 'fr', label: 'Français', flag: '\u{1F1EB}\u{1F1F7}' },
  { code: 'es', label: 'Español', flag: '\u{1F1EA}\u{1F1F8}' },
  { code: 'pt', label: 'Português', flag: '\u{1F1E7}\u{1F1F7}' },
  { code: 'fi', label: 'Suomi', flag: '\u{1F1EB}\u{1F1EE}' },
  { code: 'zh', label: '中文', flag: '\u{1F1E8}\u{1F1F3}' },
];

const NAV_GUEST = [
  { label: 'Home', to: '/', icon: 'home' },
  { label: 'Products', to: '/products', icon: 'inventory_2' },
  { label: 'Product lookup', to: '/product-finder', icon: 'manage_search' },
  { label: 'Tool finder', to: '/tool-finder', icon: 'build' },
  { label: 'Contact us', to: '/contact', icon: 'mail' },
  { label: 'Help', to: '/contact', icon: 'help' },
];

const NAV_SHOP = [
  { label: 'Home', to: '/', icon: 'home' },
  { label: 'Products', to: '/products', icon: 'inventory_2' },
  { label: 'Product lookup', to: '/product-finder', icon: 'manage_search' },
  { label: 'Tool finder', to: '/tool-finder', icon: 'build' },
  { label: 'Drill string configurator', to: '/drill-string-configurator', icon: 'settings' },
  { label: 'Compare', to: '/compare', icon: 'compare' },
];

const NAV_ACCOUNT = [
  { label: 'My orders', to: '/orders', icon: 'receipt_long' },
  { label: 'Cases', to: '/cases', icon: 'support_agent' },
  { label: 'Invoices', to: '/invoices', icon: 'receipt' },
  { label: 'Reports', to: '/reports', icon: 'assessment' },
  { label: 'Track data', to: '/track-data', icon: 'insert_drive_file' },
  { label: 'Shopping cart', to: '/cart', icon: 'shopping_cart' },
];

const NAV_SUPPORT = [
  { label: 'Contact us', to: '/contact', icon: 'mail' },
  { label: 'Technical documents', to: '/documents', icon: 'description' },
  { label: 'Help', to: '/contact', icon: 'help' },
];

const NAV_ADMIN = [
  { label: 'User management', to: '/admin/users', icon: 'group' },
  { label: 'Product management', to: '/admin/products', icon: 'edit_note' },
  { label: 'Content management', to: '/admin/content', icon: 'article' },
  { label: 'Reports', to: '/admin/reports', icon: 'analytics' },
];

function NavGroup({ title, items, currentPath, onNavigate }) {
  return (
    <div className="py-3">
      {title && (
        <p className="text-xs text-n5 font-sandvik-text tracking-wide font-normal px-4 mb-2">
          {title}
        </p>
      )}
      <nav>
        {items.map((item) => {
          const isActive = currentPath === item.to ||
            (item.to !== '/' && currentPath.startsWith(item.to));
          return (
            <Link
              key={item.label + item.to}
              to={item.to}
              onClick={onNavigate}
              className={`flex items-center gap-3 px-4 py-2.5 text-sm font-sandvik-text transition-colors
                ${isActive
                  ? 'text-electric-blue bg-eb-1/30 border-r-[3px] border-electric-blue font-normal'
                  : 'text-carbon-black hover:bg-n1'}`}
            >
              <span className={`material-symbols-outlined text-lg ${isActive ? 'text-electric-blue' : 'text-n5'}`}>
                {item.icon}
              </span>
              {item.label}
            </Link>
          );
        })}
      </nav>
    </div>
  );
}

export default function Navigation() {
  const { state, dispatch } = useApp();
  const { isAuthenticated, userType, logout } = useAuth();
  const { isMobile } = useMediaQuery();
  const location = useLocation();
  const drawerRef = useRef(null);
  const [prevPath, setPrevPath] = useState(location.pathname);

  const isOpen = state.drawerOpen;
  const isEmployee = userType === 'employee';

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [isOpen]);

  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen) {
        dispatch({ type: 'TOGGLE_DRAWER' });
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, dispatch]);

  useEffect(() => {
    if (location.pathname !== prevPath) {
      setPrevPath(location.pathname);
      if (isOpen) {
        dispatch({ type: 'TOGGLE_DRAWER' });
      }
    }
  }, [location.pathname]);

  const handleClose = () => dispatch({ type: 'TOGGLE_DRAWER' });
  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) handleClose();
  };
  const handleSignOut = () => { logout(); handleClose(); };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[60]" onClick={handleBackdropClick}
      role="dialog" aria-modal="true" aria-label="Navigation menu">
      <div className="absolute inset-0 bg-carbon-black/50" style={{ animation: 'navFadeIn 0.2s ease-out' }} />
      <div ref={drawerRef}
        className="absolute left-0 top-0 bottom-0 w-80 max-w-[85vw] bg-white shadow-xl flex flex-col overflow-hidden"
        style={{ animation: 'navSlideIn 0.25s ease-out' }}>

        <div className="flex items-center justify-between px-4 py-3 border-b border-n2 flex-shrink-0">
          <img src="/logos/sandvik_logotype_black_rgb.svg" alt="Sandvik" className="h-5"
            onError={(e) => { e.target.src = '/logos/sandvik_logotype_black_rgb300.jpg'; }} />
          <button onClick={handleClose} className="p-1.5 min-h-[44px] min-w-[44px] flex items-center justify-center hover:bg-n1 transition-colors" aria-label="Close menu">
            <span className="material-symbols-outlined text-xl text-carbon-black">close</span>
          </button>
        </div>

        <div className="flex-1 overflow-y-auto">
          <div className="px-4 py-4 border-b border-n2">
            {isAuthenticated ? (
              <AccountSwitcher />
            ) : (
              <div>
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 bg-n1 rounded-full flex items-center justify-center flex-shrink-0">
                    <span className="material-symbols-outlined text-xl text-n4">person</span>
                  </div>
                  <div>
                    <p className="text-sm font-normal font-sandvik-text text-carbon-black">Welcome</p>
                    <p className="text-xs text-n5 font-sandvik-text">Sign in for full access</p>
                  </div>
                </div>
                <Link to="/login" onClick={handleClose}
                  className="block w-full bg-electric-blue hover:bg-eb-3 text-white text-sm font-normal text-center py-2.5 rounded-none transition-colors font-sandvik-text">
                  Sign in
                </Link>
              </div>
            )}
          </div>

          {isAuthenticated ? (
            <>
              <NavGroup title="Shop" items={NAV_SHOP} currentPath={location.pathname} onNavigate={handleClose} />
              <div className="border-t border-n2" />
              <NavGroup title="My account" items={NAV_ACCOUNT} currentPath={location.pathname} onNavigate={handleClose} />
              <div className="border-t border-n2" />
              <NavGroup title="Support" items={NAV_SUPPORT} currentPath={location.pathname} onNavigate={handleClose} />
              {isEmployee && (
                <>
                  <div className="border-t border-n2" />
                  <NavGroup title="Administration" items={NAV_ADMIN} currentPath={location.pathname} onNavigate={handleClose} />
                </>
              )}
            </>
          ) : (
            <NavGroup items={NAV_GUEST} currentPath={location.pathname} onNavigate={handleClose} />
          )}

          <div className="border-t border-n2 px-4 py-4 bg-n1">
            <p className="text-xs text-n5 font-sandvik-text tracking-wide font-normal mb-3">Preferences</p>
            <div className="mb-4">
              <p className="text-xs text-n5 font-sandvik-text mb-1.5">Units of measure</p>
              <div className="flex border border-n2">
                <button onClick={() => dispatch({ type: 'SET_UOM', payload: 'metric' })}
                  className={`flex-1 py-2 text-xs font-normal font-sandvik-text transition-colors ${state.uom === 'metric' ? 'bg-vibrant-orange text-white' : 'bg-white text-n6 hover:bg-white/80'}`}>
                  mm / kg
                </button>
                <button onClick={() => dispatch({ type: 'SET_UOM', payload: 'imperial' })}
                  className={`flex-1 py-2 text-xs font-normal font-sandvik-text transition-colors ${state.uom === 'imperial' ? 'bg-vibrant-orange text-white' : 'bg-white text-n6 hover:bg-white/80'}`}>
                  in / lb
                </button>
              </div>
            </div>
            <div>
              <p className="text-xs text-n5 font-sandvik-text mb-1.5">Language</p>
              <select value={state.language}
                onChange={(e) => dispatch({ type: 'SET_LANGUAGE', payload: e.target.value })}
                className="w-full px-3 py-2 text-sm border border-n2 rounded-none font-sandvik-text text-carbon-black focus:outline-none focus:border-electric-blue bg-white">
                {LANGUAGES.map((lang) => (
                  <option key={lang.code} value={lang.code}>{lang.flag} {lang.label}</option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {isAuthenticated && (
          <div className="border-t border-n2 px-4 py-3 flex-shrink-0">
            <button onClick={handleSignOut}
              className="flex items-center gap-2 text-sm text-n6 hover:text-electric-blue transition-colors font-sandvik-text w-full py-1.5">
              <span className="material-symbols-outlined text-lg">logout</span>
              Sign out
            </button>
          </div>
        )}
      </div>

      <style>{`
        @keyframes navFadeIn { from { opacity: 0; } to { opacity: 1; } }
        @keyframes navSlideIn { from { transform: translateX(-100%); } to { transform: translateX(0); } }
      `}</style>
    </div>
  );
}
